import { ODSHelp, ODSLightDarkMode, ODSLink, ODSNotification } from '@telekom-ods/react-icons';
import { ODSAvatar, ODSButton } from '@telekom-ods/react-ui-kit';
import { type JSX } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router';

import { handleAuthNavigation } from '../../helpers/handleAuthNavigation';
import useAppContext from '../../hooks/useAppContext';
import useAuth from '../AppLayout/hooks/useAuth';
import Tooltip from '../Tooltip';

import LanguageSwitcher from './LanguageSwitcher';
import NavItem from './NavItem';

import type { Organization } from '@/types/organization';

interface BottomCollapsedItemProps {
  authenticated?: boolean;
  ownOrganizationData?: Organization | null;
}

const BottomCollapsedItem = ({
  authenticated,
  ownOrganizationData,
}: BottomCollapsedItemProps): JSX.Element => {
  const intl = useIntl();
  const { interactionInProgress, login, logout } = useAuth();
  const { toggleTheme, theme, cookiesAccepted } = useAppContext();
  const navigate = useNavigate();

  return (
    <>
      {authenticated && (
        <>
          {ownOrganizationData?._id && (
            <NavItem
              {...{
                to: '/whats-new',
                theme,
                label: intl.formatMessage({ id: 'whatsNew' }),
                icon: <ODSNotification />,
              }}
            />
          )}
          <NavItem
            {...{
              to: `/docs/${import.meta.env.VITE_CONFLUENCE_HOMEPAGE}`,
              theme,
              label: intl.formatMessage({ id: 'portalDocumentation' }),
              icon: <ODSLink />,
            }}
          />
        </>
      )}
      <NavItem
        {...{
          to: '/support-and-feedback',
          theme,
          label: intl.formatMessage({ id: 'helpAndFeedback' }),
          icon: <ODSHelp />,
        }}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <ODSAvatar
          icon={<ODSLightDarkMode />}
          name='theme-toggle'
          showBackground
          size='large'
          variant='icon'
          onClick={toggleTheme}
        />

        <LanguageSwitcher />
        <Tooltip
          key='sidebar-login-logout'
          title={!cookiesAccepted ? intl.formatMessage({ id: 'privacyPolicyConfirmation' }) : ''}
        >
          <ODSButton
            className='rounded-button'
            label={
              authenticated
                ? intl.formatMessage({ id: 'logout' })
                : intl.formatMessage({ id: 'login' })
            }
            size='small'
            variant='outline'
            disabled={!cookiesAccepted || interactionInProgress}
            onClick={() => {
              if (authenticated) {
                logout();
              } else {
                handleAuthNavigation({ authenticated, cookiesAccepted, login, navigate });
              }
            }}
          />
        </Tooltip>
      </div>
    </>
  );
};

export default BottomCollapsedItem;
